/**
 * Firebase (Google Identity Platform) client initialization.
 *
 * GCIP multi-tenant: one GCIP tenant per agency. The Web SDK is configured
 * from non-secret public config (NEXT_PUBLIC_FIREBASE_*) baked at build time.
 * Tenant routing is per-request: callers set `auth.tenantId = gcip_tenant_id`
 * (resolved from the login slug / email domain) before signing in.
 *
 * See DECISIONS.md 2026-06-16 and CJIS_EXCEPTIONS.md PE-21.
 */

'use client';

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import { getAuth, type Auth } from 'firebase/auth';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _appCheck: AppCheck | null = null;
let _appCheckTried = false;

function buildConfig() {
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '';
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '';
  const authDomain =
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ||
    (projectId ? `${projectId}.firebaseapp.com` : '');
  const appId = process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '';
  const appCheckSiteKey = process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_SITE_KEY || '';
  return { apiKey, projectId, authDomain, appId, appCheckSiteKey };
}

export function isFirebaseConfigured(): boolean {
  const { apiKey, projectId } = buildConfig();
  return Boolean(apiKey && projectId);
}

// Initialize Firebase App Check (reCAPTCHA Enterprise) once, in the browser
// only. App Check attaches an attestation token to Identity Platform data-plane
// calls; when GCIP enforcement is ON, tokenless callers are rejected. Wiring is
// additive: if appId or the site key is absent (e.g. a build before the
// firebase-app-id / firebase-appcheck-site-key secrets were seeded), App Check
// is simply not initialized and sign-in proceeds unattested.
function ensureAppCheck(app: FirebaseApp): void {
  if (_appCheckTried || typeof window === 'undefined') return;
  _appCheckTried = true;
  const { appId, appCheckSiteKey } = buildConfig();
  if (!appId || !appCheckSiteKey) return;

  // Local dev escape hatch: localhost is not in the reCAPTCHA Enterprise key's
  // allowed_domains, so real attestation tokens are rejected once GCIP App Check
  // enforcement is ON (auth/firebase-app-check-token-is-invalid). A registered
  // debug token (Firebase console -> App Check -> Manage debug tokens) lets a
  // developer mint VALID tokens from localhost. The global MUST be set before
  // initializeAppCheck. Development builds only; the branch is dead-code-
  // eliminated from production bundles (NODE_ENV inlined to a constant).
  if (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG_TOKEN
  ) {
    const debugToken = process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG_TOKEN;
    (globalThis as unknown as {
      FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean;
    }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
  }

  try {
    _appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: false,
    });
  } catch {
    // App Check init is best-effort; never block sign-in on it.
    _appCheck = null;
  }
}

export function getFirebaseApp(): FirebaseApp {
  if (_app) {
    ensureAppCheck(_app);
    return _app;
  }
  const { apiKey, projectId, authDomain, appId } = buildConfig();
  if (!apiKey || !projectId) {
    throw new Error(
      'Firebase not configured: NEXT_PUBLIC_FIREBASE_API_KEY and NEXT_PUBLIC_FIREBASE_PROJECT_ID required'
    );
  }
  _app = getApps().length
    ? getApp()
    : initializeApp({ apiKey, authDomain, projectId, ...(appId ? { appId } : {}) });
  ensureAppCheck(_app);
  return _app;
}

export function getFirebaseAuth(): Auth {
  if (_auth) return _auth;
  _auth = getAuth(getFirebaseApp());
  return _auth;
}

// The App Check instance (or null if not initialized). Exposed mainly so the
// module-level handle is retained; callers rarely need it directly since tokens
// are auto-attached to Firebase service calls.
export function getFirebaseAppCheck(): AppCheck | null {
  return _appCheck;
}
